import { SubmitFormButton, useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { DeleteIcon } from '@calendly/ui/components/icons'
import { Footer } from '@calendly/ui/components/popup'
import { useDispatch, useSelector } from 'react-redux'

import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { countriesWithNoPostalCode } from './countries_with_no_postal_code'
import { resetPopup, setDeleteVisibility } from './slice'
import { ButtonWrapper } from './styles'
import {
  taxInformationModalConfirmedTaxInformationSelector,
  taxInformationModalShouldUpdateExistingSubscriptionSelector,
} from '../../selectors'
import { secondaryTaxIdSelector, taxIdSelector } from '../../stages/paid_stage/billing_information_card/selectors'


export const FormFooter = () => {
  const { values } = useFormContext()
  const dispatch = useDispatch()
  const track = useTrack()
  const taxId = useSelector(taxIdSelector)
  const secondaryTaxId = useSelector(secondaryTaxIdSelector)
  const confirmedTaxInfo = useSelector(taxInformationModalConfirmedTaxInformationSelector)
  const shouldUpdateExistingSubscription = useSelector(taxInformationModalShouldUpdateExistingSubscriptionSelector)

  const areRequiredFieldsFilled = () => {
    const requiredFields = [
      'countryCode',
      'streetAddress',
      'city',
      'companyName'
    ]

    const taxIdFields = [
      'taxId',
      'canadaFederalTaxId',
      'canadaProvincialTaxId',
      'australiaTaxId'
    ]

    // Conditionally add 'postalCode' to required fields based on countryCode
    const isPostalCodeRequired = !countriesWithNoPostalCode.includes(values.countryCode)
    if (isPostalCodeRequired) requiredFields.push('postalCode')

    const requiredFieldsFilled = requiredFields.every(
      (field) => values[field] && values[field].trim() !== ''
    )

    // Conditionally check 'stateProvince' based on 'countryCode'
    const isStateProvinceRequired = ['CA', 'IN'].includes(values.countryCode)
    const stateProvinceFilled = !isStateProvinceRequired
      || (values.stateProvince && values.stateProvince.trim() !== '')

    const isAnyTaxIdFilled = taxIdFields.some(
      (field) => values[field] && values[field].trim() !== ''
    )

    return requiredFieldsFilled && stateProvinceFilled && isAnyTaxIdFilled
  }

  const deleteButton = () => {
    if (values.countryCode === 'US') return null

    return (
      <Button
        decoration="secondary-outline"
        onClick={() => dispatch(setDeleteVisibility(true))}
        spacingRight="16"
      >
        <DeleteIcon />
        {I18n.t('publisher.billing.vat_id_popup.delete_popup.delete_button')}
      </Button>
    )
  }

  const clearButton = () => (
    <Button
      decoration="secondary-outline"
      onClick={() => {
        track({
          name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
          properties: {
            action: 'clearing_info',
            location: 'new_customer_checkout_flow',
            modal_name: 'edit_tax_info'
          }
        })
        dispatch(resetPopup())
      }}
      spacingRight="16"
    >
      {I18n.t('publisher.billing.vat_id_popup.clear_info')}
    </Button>
  )

  const button = () => {
    if (values.countryCode === 'US') {
      return (
        <Button onClick={() => dispatch(resetPopup())}>
          {I18n.t('publisher.billing.vat_id_popup.success.close')}
        </Button>
      )
    }

    return (
      <SubmitFormButton disabled={!areRequiredFieldsFilled()}>
        {(taxId || secondaryTaxId)
          ? I18n.t('publisher.billing.vat_id_popup.save_changes')
          : I18n.t('publisher.billing.vat_id_popup.submit_button')}
      </SubmitFormButton>
    )
  }

  return (
    <Footer>
      <div />
      <ButtonWrapper>
        {(shouldUpdateExistingSubscription && (taxId || secondaryTaxId)) && deleteButton()}
        {(!shouldUpdateExistingSubscription && confirmedTaxInfo) && clearButton()}
        {button()}
      </ButtonWrapper>
    </Footer>
  )
}
